import React, { useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, Typography } from '@material-ui/core'
import { Alert, AlertTitle } from '@material-ui/lab'

import { ControlsSection } from 'components'

import helpers from 'helpers'

import useDocumentGenerate from 'hooks/useDocumentGenerate'
import useFetch from 'hooks/useFetch'
import usePagination from 'hooks/usePagination'

import theme from 'theme'
import * as service from 'service'
import constants from 'constants/index'

const LINKING_STATE = 'linking'
const LINKED_STATE = 'linked'
const FAILED_STATE = 'failed'
const NOT_ACTIVE_STATE = 'notActive'

const LinkControlsStep = () => {
  const { ACTIVE_STATUS_ID } = constants.documents

  const {
    controlOrigin,
    documentGenerated,
    handleNext,
    setDisabledNextButton,
    wantsToAdvanceStep,
  } = useDocumentGenerate()

  const documentId = documentGenerated?.id

  const [autoLinkState, setAutoLinkState] = useState(null)
  const [autoLinkError, setAutoLinkError] = useState('')

  // O vínculo automático é um efeito colateral que só pode acontecer uma vez por fluxo: sem essa
  // trava um novo render (ou o refresh da listagem) tentaria vincular a mesma evidência de novo.
  const autoLinkStarted = useRef(false)

  const { page, perPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ initialPerPage: 5 })

  const { response, isLoading, refresh } = useFetch(
    service.dponet.privacyPolicies.listLinkedDocuments,
    { privacyPolicyId: documentId, page, perPage },
    [documentId, page, perPage],
    !!documentId,
  )

  const linkedDocuments = response?.data?.linkedDocuments || []

  const isLinking = autoLinkState === LINKING_STATE

  const linkControlOrigin = async () => {
    try {
      setAutoLinkState(LINKING_STATE)

      await service.dponet.privacyPolicies.createLinkedDocuments({
        privacyPolicyId: documentId,
        evidenceIds: [controlOrigin?.evidenceId],
      })

      setAutoLinkState(LINKED_STATE)
      refresh()
    } catch (error) {
      setAutoLinkState(FAILED_STATE)
      setAutoLinkError(
        helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Não foi possível vincular o documento ao controle de origem.',
      )
    }
  }

  // Avançar no meio da operação deixaria o vínculo pendurado: o PDF é gerado de forma síncrona no
  // backend (LinkedDocuments::FindOrCreate -> DocumentVersions::FindOrExport), então o botão de
  // próximo só volta a ficar disponível quando termina.
  useEffect(() => {
    setDisabledNextButton(isLinking)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLinking])

  useEffect(() => {
    if (!controlOrigin?.evidenceId || !documentId) return
    if (autoLinkStarted.current) return

    autoLinkStarted.current = true

    // Só documento ativo pode ser vinculado como evidência. Quando o usuário pediu aprovação na etapa
    // anterior o documento fica aguardando aprovação, e o vínculo precisa ser feito depois — por aqui
    // ou pela aba Controles da tela do documento.
    if (documentGenerated?.status !== ACTIVE_STATUS_ID) {
      return setAutoLinkState(NOT_ACTIVE_STATE)
    }

    linkControlOrigin()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlOrigin, documentId, documentGenerated?.status])

  useEffect(() => {
    if (wantsToAdvanceStep) handleNext()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsToAdvanceStep])

  return (
    <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
      {isLinking && (
        <Alert
          severity="info"
          variant="standard"
          icon={<CircularProgress size={20} />}
        >
          <AlertTitle>Gerando o arquivo e vinculando ao controle</AlertTitle>
          <Typography>
            Estamos gerando o arquivo do documento e vinculando ele à atividade
            do projeto de governança em que a criação começou. Isso pode levar
            alguns segundos — <strong>não feche esta tela</strong>.
          </Typography>
        </Alert>
      )}
      {autoLinkState === LINKED_STATE && (
        <Alert severity="success" variant="standard">
          <AlertTitle>Documento vinculado ao controle de origem</AlertTitle>
          <Typography>
            Este documento já foi vinculado como evidência do controle em que a
            criação começou. Você pode vinculá-lo a outros controles abaixo.
          </Typography>
        </Alert>
      )}
      {autoLinkState === NOT_ACTIVE_STATE && (
        <Alert severity="warning" variant="standard">
          <AlertTitle>
            O vínculo com o controle será possível após a aprovação
          </AlertTitle>
          <Typography>
            Somente documentos ativos podem ser vinculados como evidência. Como
            este documento ficou aguardando aprovação, faça o vínculo depois que
            ele for aprovado — pela aba Controles da tela do documento ou pelo
            próprio controle no projeto de governança.
          </Typography>
        </Alert>
      )}
      {autoLinkState === FAILED_STATE && (
        <Alert severity="error" variant="standard">
          <AlertTitle>
            Não foi possível vincular o documento ao controle de origem
          </AlertTitle>
          <Typography>{autoLinkError}</Typography>
        </Alert>
      )}
      {!controlOrigin && (
        <Alert severity="info" variant="standard">
          <AlertTitle>Vincular controles é opcional</AlertTitle>
          <Typography>
            Você pode vincular controles de projetos de governança a este
            documento agora ou pular esta etapa e vinculá-los depois, na tela de
            edição do documento.
          </Typography>
        </Alert>
      )}
      <ControlsSection
        documentId={documentId}
        documentStatus={documentGenerated?.status}
        newControlDisabled={isLinking}
        newControlTooltip={
          isLinking
            ? 'Aguarde o vínculo automático com o controle de origem terminar.'
            : ''
        }
        controls={{
          linkedDocuments,
          isLoading: isLoading || isLinking,
          refresh,
          pagination: {
            page,
            perPage,
            totalCount: response?.data?.meta?.totalCount,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          },
        }}
      />
    </Box>
  )
}

export default LinkControlsStep
